import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import hadiyaLogo from '../assets/hadiya-logo-web.png';

export default function DailyResults() {
  const [results, setResults] = useState([]);
  const [student, setStudent] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const isTestChannel = queryParams.get('testChannel') === 'true';
        const response = await api.get(`/exam/daily-quiz-results?testChannel=${isTestChannel}`);
        setResults(response.data.results);
        setTotalScore(response.data.total_score);
        setStudent(response.data.student);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/exam');
        } else {
          setError(err.response?.data?.error || 'Failed to fetch results');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [navigate]);

  return (
    <div style={styles.container}>
      {/* Premium Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <img src={hadiyaLogo} alt="Hadiya Logo" style={styles.logo} />
          <h1 style={styles.title}>Daily Quiz Results</h1>
        </div>
        <button 
          onClick={() => {
            const isTest = new URLSearchParams(window.location.search).get('testChannel') === 'true';
            navigate(isTest ? '/student-react/test-channel' : '/student-react/dashboard');
          }} 
          style={styles.backBtn}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px'}}>
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Dashboard
        </button>
      </header>

      <div style={styles.content}>
        {loading ? (
          <div style={styles.loadingContainer}>
             <div style={styles.spinner}></div>
             <p>Loading your performance...</p>
          </div>
        ) : error ? (
          <div style={styles.errorContainer}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom: '12px'}}>
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>{error}</p>
          </div>
        ) : (
          <>
            {/* Top Cards - Full Width Layout */}
            <div style={styles.topCardsContainer}>
              {/* Student Details Card (Top, Full Width) */}
              {student && (
                <div style={styles.studentCardFull}>
                  <div style={styles.studentHeader}>
                    <div style={styles.studentAvatar}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <div style={styles.studentInfo}>
                      <h3 style={styles.studentName}>{student.name}</h3>
                      <div style={styles.studentMetaGroup}>
                        <span style={styles.metaBadge}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px'}}>
                            <path d="M22 9 12 4 2 9v2h2v10h16V11h2z"></path>
                            <path d="M10 21v-6h4v6"></path>
                          </svg>
                          {student.classroom}
                        </span>
                        <span style={styles.metaBadge}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '6px'}}>
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="2" y1="12" x2="22" y2="12"></line>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                          </svg>
                          {student.nation}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Overall Performance Card (Below) */}
              <div style={styles.kpiCardPrimary}>
                <div style={styles.kpiHeader}>
                  <div style={styles.kpiIconWrapper}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20V10"></path>
                      <path d="M18 20V4"></path>
                      <path d="M6 20v-4"></path>
                    </svg>
                  </div>
                  <h2 style={styles.kpiTitle}>Overall Performance</h2>
                </div>
                <div style={styles.kpiValueWrapper}>
                  <span style={styles.kpiLabel}>Total Score</span>
                  <span style={styles.kpiScore}>{totalScore}</span>
                </div>
              </div>
            </div>

            {/* Daily Breakdown List */}
            <h3 style={styles.sectionTitle}>Daily Breakdown</h3>
            <div style={styles.resultList}>
              {results.length > 0 ? (
                results.map((r, i) => (
                  <div key={r.quiz_id} style={styles.resultCard}>
                    <div style={styles.resultCardLeft}>
                      <span style={styles.dayBadge}>Day {i + 1}</span>
                      <h4 style={styles.quizName}>{r.quiz_name}</h4>
                    </div>
                    <div style={styles.resultCardRight}>
                      <div style={styles.statGroup}>
                        <span style={styles.statLabel}>Attended</span>
                        <span style={styles.statValue}>{r.attended}</span>
                      </div>
                      <div style={styles.statDivider}></div>
                      <div style={styles.statGroup}>
                        <span style={styles.statLabel}>Score</span>
                        <span style={styles.scoreValue}>{r.score}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={styles.emptyState}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom: '16px'}}>
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                  </svg>
                  <p>No daily quizzes attended yet.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F8FAFC',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(12px)',
    padding: '16px 5%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 40,
    borderBottom: '1px solid #E2E8F0'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  logo: {
    height: '36px'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    color: '#1E293B',
    fontWeight: '700',
    letterSpacing: '-0.01em'
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    backgroundColor: '#F1F5F9',
    color: '#475569',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    transition: 'all 0.2s',
  },
  content: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '32px 5%',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px',
    color: '#64748B',
    fontWeight: '500'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #E2E8F0',
    borderTop: '3px solid #3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px',
    backgroundColor: '#FEF2F2',
    color: '#DC2626',
    borderRadius: '16px',
    border: '1px solid #FECACA',
    fontWeight: '500'
  },
  topCardsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  studentCardFull: {
    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.02)',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box'
  },
  kpiCardPrimary: {
    background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
    borderRadius: '24px',
    padding: '40px 32px',
    color: 'white',
    boxShadow: '0 12px 28px rgba(37, 99, 235, 0.25)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    boxSizing: 'border-box',
    alignItems: 'center',
    textAlign: 'center'
  },
  kpiHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px'
  },
  kpiIconWrapper: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: '16px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)'
  },
  kpiTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: '700',
    letterSpacing: '-0.01em',
    color: '#E0E7FF'
  },
  kpiValueWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  },
  kpiLabel: {
    fontSize: '15px',
    color: '#93C5FD',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontWeight: '700'
  },
  kpiScore: {
    fontSize: '64px',
    fontWeight: '800',
    lineHeight: '1',
    letterSpacing: '-0.03em',
    textShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  studentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    width: '100%'
  },
  studentAvatar: {
    width: '72px',
    height: '72px',
    background: 'linear-gradient(135deg, #E0E7FF 0%, #DBEAFE 100%)',
    color: '#2563EB',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.5), 0 4px 10px rgba(37,99,235,0.1)'
  },
  studentInfo: {
    flex: 1
  },
  studentName: {
    margin: '0 0 10px 0',
    fontSize: '26px',
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: '-0.02em'
  },
  studentMetaGroup: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap'
  },
  metaBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: 'white',
    color: '#334155',
    padding: '8px 16px',
    borderRadius: '99px',
    fontSize: '14px',
    fontWeight: '600',
    border: '1px solid #E2E8F0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#0F172A',
    margin: '16px 0 0 0',
    letterSpacing: '-0.01em'
  },
  resultList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  resultCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    border: '1px solid #E2E8F0',
    transition: 'transform 0.2s, box-shadow 0.2s',
    flexWrap: 'wrap',
    gap: '16px'
  },
  resultCardLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: '1 1 200px'
  },
  dayBadge: {
    fontSize: '12px',
    textTransform: 'uppercase',
    fontWeight: '700',
    letterSpacing: '0.05em',
    color: '#64748B'
  },
  quizName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#1E293B',
    lineHeight: '1.4'
  },
  resultCardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    backgroundColor: '#F8FAFC',
    padding: '12px 24px',
    borderRadius: '12px',
    border: '1px solid #F1F5F9'
  },
  statGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
  },
  statLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  statValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#334155'
  },
  scoreValue: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#059669' // Emerald 600
  },
  statDivider: {
    width: '1px',
    height: '32px',
    backgroundColor: '#E2E8F0'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px dashed #CBD5E0',
    color: '#A0AEC0',
    fontWeight: '500'
  }
};
